import { Link } from 'react-router-dom';
import { useDashboard, type Dashboard as DashboardData } from '../api';
import { formatInteractionType } from '../interactionTypes';

function stripHtml(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '').trim();
}

export default function Dashboard() {
  const { data, isLoading, error } = useDashboard();

  if (isLoading) return <p className="text-slate-500 dark:text-slate-400">Loading…</p>;
  if (error) return <p className="text-red-600 dark:text-red-400">Error loading dashboard.</p>;
  if (!data) return null;

  const months = data.trendline.map((t) => t.month);
  const householdSeries = data.trendline.map((t) => t.households_total);
  const peopleSeries = data.trendline.map((t) => t.people_total);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <PopulationCard
          households={data.counts.households}
          people={data.counts.people}
          householdSeries={householdSeries}
          peopleSeries={peopleSeries}
          months={months}
        />
        {data.ministering && <MinisteredCard ministering={data.ministering} />}
        {data.ministering && (
          <CoverageCard
            assigned={data.ministering.assigned_households}
            total={data.counts.households}
          />
        )}
        {data.ward_coverage && <WardShareCard coverage={data.ward_coverage} />}
        <SyncCard sync={data.last_sync} />
      </div>

      {data.activity && <ActivityStrip activity={data.activity} />}

      <Trendline data={data.trendline} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <RecentList
          title="Longest since contact"
          empty="No assigned households."
          items={(data.ministering?.longest_uncontacted ?? []).map((h) => ({
            id: h.id,
            primary: h.name_directory,
            secondary: stripHtml(h.formatted_address),
            tertiary: h.last_contact_at
              ? `last contact ${relTime(h.last_contact_at)}`
              : 'never contacted',
          }))}
        />
        <RecentList
          title="Recent move-ins (30d)"
          empty="No new households in the last 30 days."
          items={data.recent_move_ins.map((h) => ({
            id: h.id,
            primary: h.name_directory,
            secondary: stripHtml(h.formatted_address),
            tertiary: relTime(h.created_at),
          }))}
        />
        <RecentList
          title="Recent move-outs (30d)"
          empty="No move-outs in the last 30 days."
          items={data.recent_move_outs.map((h) => ({
            id: h.id,
            primary: h.name_directory,
            secondary: stripHtml(h.formatted_address),
            tertiary: relTime(h.removed_at),
          }))}
        />
      </div>
    </div>
  );
}

function PopulationCard({
  households,
  people,
  householdSeries,
  peopleSeries,
  months,
}: {
  households: number;
  people: number;
  householdSeries: number[];
  peopleSeries: number[];
  months: string[];
}) {
  return (
    <div className="grid grid-cols-2 divide-x divide-slate-200 dark:divide-slate-700 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
      <MiniStat label="Households" value={households} series={householdSeries} months={months} />
      <MiniStat label="Individuals" value={people} series={peopleSeries} months={months} className="pl-4" />
    </div>
  );
}

function MiniStat({
  label,
  value,
  series,
  months,
  className = '',
}: {
  label: string;
  value: number;
  series: number[];
  months: string[];
  className?: string;
}) {
  const first = series[0];
  const last = series[series.length - 1];
  const delta = first != null && last != null ? last - first : null;
  return (
    <div className={className}>
      <div className="flex items-baseline justify-between gap-2 pr-3">
        <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
        {delta != null && delta !== 0 && (
          <span
            className={`text-[11px] tabular-nums ${
              delta > 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'
            }`}
            title={`${delta > 0 ? '+' : ''}${delta} over the last 12 months`}
          >
            {delta > 0 ? '+' : ''}{delta}
          </span>
        )}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
        {value.toLocaleString()}
      </div>
      {series.length > 1 && (
        <div className="mt-1">
          <Sparkline values={series} months={months} width={72} height={20} ariaLabel={`${label} trend`} />
        </div>
      )}
    </div>
  );
}

function MinisteredCard({ ministering }: { ministering: DashboardData['ministering'] }) {
  const { assigned_households: assigned, ministered_90d: ministered, trend } = ministering;
  const pct = assigned > 0 ? Math.round((ministered / assigned) * 100) : null;
  // Rolling-90d percentage per month; months before ministering data was
  // loaded have assigned = 0 and are skipped.
  const withData = trend.filter((t) => t.assigned > 0);
  const pctSeries = withData.map((t) => Math.round((t.ministered / t.assigned) * 100));
  const pctMonths = withData.map((t) => t.month);
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Ministered · rolling 90d
      </div>
      <div className="mt-1 flex items-end justify-between gap-3">
        <div>
          <div className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {pct != null ? `${pct}%` : '—'}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {ministered} of {assigned} assigned households contacted
          </div>
        </div>
        {pctSeries.length > 1 && (
          <Sparkline
            values={pctSeries}
            months={pctMonths}
            width={72}
            height={20}
            ariaLabel="Ministered percentage trend"
          />
        )}
      </div>
    </div>
  );
}

function WardShareCard({ coverage }: { coverage: NonNullable<DashboardData['ward_coverage']> }) {
  const {
    households_in_boundary: inBoundary, housing_units: units, share,
    census_generated: censusAt, history,
  } = coverage;
  // Only points with a denominator can be plotted; a snapshot taken before
  // the census existed has a real household count and no percentage, and
  // drawing it as zero would invent a collapse that never happened.
  const plotted = history.filter((h) => h.share != null);
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Share of ward homes
      </div>
      <div className="mt-1 flex items-end justify-between gap-3">
        <div>
          <div className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {share != null ? `${share}%` : '—'}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {units != null
              ? <>{inBoundary} of {units} homes in the boundary</>
              : <>{inBoundary} households · run tools/refresh_housing_units</>}
          </div>
        </div>
        {plotted.length > 1 && (
          <Sparkline
            values={plotted.map((h) => h.share as number)}
            months={plotted.map((h) => h.as_of)}
            width={72}
            height={20}
            ariaLabel="Share of ward homes over time"
          />
        )}
      </div>
      {censusAt && (
        <div className="mt-1 text-[10px] text-slate-400 dark:text-slate-500">
          homes counted {censusAt}
        </div>
      )}
    </div>
  );
}

function CoverageCard({ assigned, total }: { assigned: number; total: number }) {
  const pct = total > 0 ? Math.round((assigned / total) * 100) : null;
  const unassigned = total - assigned;
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Assignment coverage
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
        {pct != null ? `${pct}%` : '—'}
      </div>
      <div className="text-xs text-slate-500 dark:text-slate-400">
        {assigned} of {total} households have ministers
        {unassigned > 0 && <> · {unassigned} unassigned</>}
      </div>
    </div>
  );
}

function Sparkline({
  values,
  months,
  width = 96,
  height = 28,
  ariaLabel,
}: {
  values: number[];
  months?: string[];
  width?: number;
  height?: number;
  ariaLabel?: string;
}) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const padY = 2;
  const w = width;
  const h = height;
  const step = w / (values.length - 1);
  const points = values.map((v, i) => {
    const x = i * step;
    const y = h - padY - ((v - min) / span) * (h - padY * 2);
    return [x, y] as const;
  });
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${path} L${points[points.length - 1][0].toFixed(1)},${h} L0,${h} Z`;
  const last = points[points.length - 1];
  const trend = values[values.length - 1] - values[0];
  const tone = trend > 0
    ? { line: 'stroke-emerald-600 dark:stroke-emerald-400', dot: 'fill-emerald-600 dark:fill-emerald-400', area: 'fill-emerald-100 dark:fill-emerald-900/60' }
    : trend < 0
      ? { line: 'stroke-rose-600 dark:stroke-rose-400', dot: 'fill-rose-600 dark:fill-rose-400', area: 'fill-rose-100 dark:fill-rose-900/60' }
      : { line: 'stroke-slate-500 dark:stroke-slate-400', dot: 'fill-slate-500 dark:fill-slate-400', area: 'fill-slate-200 dark:fill-slate-700' };
  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      className="overflow-visible"
    >
      <title>
        {months && months.length === values.length
          ? `${months[0]} → ${months[months.length - 1]}: ${values[0]} → ${values[values.length - 1]}`
          : `${values[0]} → ${values[values.length - 1]}`}
      </title>
      <path d={areaPath} className={tone.area} opacity={0.5} />
      <path d={path} fill="none" className={tone.line} strokeWidth={1.25} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r={1.75} className={tone.dot} />
    </svg>
  );
}

function SyncCard({ sync }: { sync: ReturnType<typeof useDashboard>['data'] extends infer D
  ? D extends { last_sync: infer S } ? S : never : never }) {
  if (!sync) {
    return (
      <Link
        to="/syncs"
        className="block rounded border border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 p-4 text-sm text-rose-800 dark:text-rose-200 hover:bg-rose-100 dark:hover:bg-rose-900/40"
      >
        <div className="text-xs uppercase tracking-wide">Last LCR sync</div>
        <div className="mt-1">No LCR sync recorded yet.</div>
      </Link>
    );
  }
  const ageDays = (Date.now() - new Date(sync.finished_at).getTime()) / 86_400_000;
  const tone = ageDays >= 14 ? RED : ageDays >= 7 ? YELLOW : GREEN;
  return (
    <Link to="/syncs" className={`block rounded border p-4 ${tone.card}`}>
      <div className={`text-xs uppercase tracking-wide ${tone.label}`}>Last LCR sync</div>
      <div className="mt-1 text-sm">
        <span className={tone.status}>{sync.status}</span> · {relTime(sync.finished_at)}
      </div>
      <div className={`mt-2 text-xs ${tone.detail}`}>
        +{sync.households_upserted}/-{sync.households_removed} households · +
        {sync.people_upserted}/-{sync.people_removed} people · +
        {sync.callings_opened}/-{sync.callings_closed} callings
      </div>
    </Link>
  );
}

const GREEN = {
  card: 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/40',
  label: 'text-emerald-700 dark:text-emerald-300',
  status: 'text-emerald-800 dark:text-emerald-300 font-medium',
  detail: 'text-emerald-700/80 dark:text-emerald-300/80',
};
const YELLOW = {
  card: 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-900/40',
  label: 'text-amber-700 dark:text-amber-300',
  status: 'text-amber-800 dark:text-amber-300 font-medium',
  detail: 'text-amber-700/80 dark:text-amber-300/80',
};
const RED = {
  card: 'border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/40',
  label: 'text-rose-700 dark:text-rose-300',
  status: 'text-rose-800 dark:text-rose-200 font-medium',
  detail: 'text-rose-700/80 dark:text-rose-300/80',
};

function ActivityStrip({ activity }: { activity: DashboardData['activity'] }) {
  const series = activity.monthly.map((m) => m.interactions);
  const months = activity.monthly.map((m) => m.month);
  const byType = Object.entries(activity.this_month.by_type);
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Ministering activity
      </div>
      <div className="text-sm text-slate-700 dark:text-slate-300">
        <span className="text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">
          {activity.this_month.total}
        </span>{' '}
        <span className="text-xs text-slate-500 dark:text-slate-400">
          this month
          {byType.length > 0 && (
            <> — {byType.map(([t, n]) => `${n} ${formatInteractionType(t).toLowerCase()}`).join(' · ')}</>
          )}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <Sparkline values={series} months={months} width={120} height={24} ariaLabel="Interactions per month" />
        <span className="text-[10px] text-slate-400 dark:text-slate-500">per month, last 12</span>
        <Link to="/interactions" className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:underline">
          View log →
        </Link>
      </div>
    </div>
  );
}

function Trendline({ data }: { data: { month: string; households_added: number; people_added: number }[] }) {
  const max = Math.max(1, ...data.map((d) => Math.max(d.households_added, d.people_added)));
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
      <div className="mb-2 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Adds per month (last 12)
      </div>
      <div className="grid grid-cols-12 gap-1">
        {data.map((d) => (
          <div key={d.month} className="flex flex-col items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
            <div className="flex h-24 w-full items-end justify-center gap-px">
              <div
                title={`households +${d.households_added}`}
                className="w-2 bg-slate-900 dark:bg-slate-700"
                style={{ height: `${(d.households_added / max) * 96}px` }}
              />
              <div
                title={`people +${d.people_added}`}
                className="w-2 bg-emerald-500"
                style={{ height: `${(d.people_added / max) * 96}px` }}
              />
            </div>
            <div>{d.month.slice(5)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface RecentItem {
  id: string;
  primary: string | null;
  secondary: string | null;
  tertiary: string;
}

function RecentList({ title, empty, items }: { title: string; empty: string; items: RecentItem[] }) {
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-2 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {title}
      </div>
      {items.length === 0 ? (
        <p className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{empty}</p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((it) => (
            <li key={it.id} className="px-4 py-2 text-sm">
              <Link to={`/households/${it.id}`} className="font-medium text-slate-800 dark:text-slate-200 hover:underline">
                {it.primary ?? '(no name)'}
              </Link>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {it.secondary} · {it.tertiary}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 60) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}
